import { useDebouncedCallback } from '@speedlo/hooks'
import { PictogramIcon } from '@speedlo/ui'
import { observer } from 'mobx-react-lite'
import React from 'react'
import styled from 'styled-components/macro'

import { TTheme } from '../../app/ThemeSwitchProvider'
import { useRoot } from '../../app/useRoot'
import { useMap } from '../../components/Map/useMap'
import { latLngToGps } from '../../helpers/gps'
import { useLocationResolver } from '../../hooks/useLocationResolver'
import { useFoodMenuQuery } from '../foodMenu/hooks/useFoodMenuQuery'
import { AddressSubmit } from './AddressSubmit'
import { DeliveryAddressSearch } from './DeliveryAddressSearch'
import { DeliveryInitialLocation } from './DeliveryInitialLocation'
import { useOrderCreate } from './hooks/useOrderCreate'

type TProps = NoChildren & {
  onConfirm(): void
}

const StyledDeliveryMap = styled.div`
  .delivery__map__address {
    position: absolute;
    top: 4rem;
    left: 2.3rem;
    right: 2.3rem;
    z-index: 1000;
  }
  .delivery__map__submit {
    position: absolute;
    bottom: 3rem;
    width: 100%;
    z-index: 1000;
  }
  .pin {
    position: absolute;
    z-index: 10000;
    top: calc(50% - 5px);
    left: 50%;
    margin-left: -21px;
    margin-top: 9px;
    pointer-events: none;

    &::before {
      font-size: 2.5rem;
      margin: 0;
      color: ${({ theme }) => (theme as TTheme).primary};
    }
  }
`

export const DeliveryMap = observer<TProps>(({ onConfirm }) => {
  const { location } = useRoot()
  const { map } = useMap()
  const { createDeliveryOrder } = useOrderCreate(onConfirm)

  // debouncing so user can pan map more before resolving kicks in
  const [resolver] = useDebouncedCallback(useLocationResolver(), 500)
  const resolveToMapCenter = React.useCallback(() => {
    const center = map?.getCenter()
    if (center) {
      resolver(latLngToGps(center), null)
    }
  }, [map, resolver])

  // initial location resolve
  React.useEffect(() => {
    resolveToMapCenter()
  }, [resolveToMapCenter])

  React.useEffect(() => {
    if (map) {
      map.on('dragend', resolveToMapCenter)
      // * causing weird behavior where resolution is run again after search
      // map.on('moveend', resolveToMapCenter)
      return () => {
        map.off('dragend', resolveToMapCenter)
        // map.off('moveend', resolveToMapCenter)
      }
    }
    return
  })

  // prefetch food menu when correct address is resolved
  useFoodMenuQuery(location.isDeliveryAddress ? location.branchId : null)

  return (
    <StyledDeliveryMap data-testid="DeliverMap">
      <DeliveryInitialLocation />
      <PictogramIcon icon="signs" className="pin" />
      <div className="delivery__map__address">
        <DeliveryAddressSearch />
      </div>
      <div className="delivery__map__submit">
        <AddressSubmit
          enabled={location.isDeliveryAddress && !location.isResolving}
          onClick={createDeliveryOrder}
        />
      </div>
    </StyledDeliveryMap>
  )
})
